import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MenuBarItemEnum } from '../../../../shared/enums/menu-bar-items.enum';
import { MenuItem } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { IToken } from '../../../../shared/interfaces/token.interface';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SeverityEnum } from '../../../../shared/enums/severity.enum';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ApplicationSettingsDialogComponent } from '../../../dialogs/application-settings-dialog/application-settings-dialog.component';
import { ApplicationSettingsService } from '../../../../shared/services/applicationsettings.service';
import { Subject, Subscription, of, switchMap, takeUntil } from 'rxjs';
import {
  IGetApplicationSettings,
  IUpdateApplicationSettingsRequest,
} from '../../../../shared/interfaces/application-settings.interface';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorService } from '../../../../shared/services/error.service';
import { CountryService } from '../../../../shared/services/country.service';
import { IGetCountries } from '../../../../shared/interfaces/country.interface';
import { INamedEntity } from '../../../../shared/interfaces/namedEntity.interface';
import { Roles } from '../../../../shared/enums/roles.enum';
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private authenticationService = inject(AuthenticationService);
  private notificationService = inject(NotificationService);
  private applicationSettingsService = inject(ApplicationSettingsService);
  private dialogService = inject(DialogService);
  private errorService = inject(ErrorService);
  private countryService = inject(CountryService);
  private destroy$: Subject<void> = new Subject<void>();

  items: MenuItem[] = [];
  selectedItem: unknown;
  loggedUser!: string;
  countries!: INamedEntity[];
  selectedCountryId!: number;

  countries$ = this.countryService.getCountries().pipe(
    takeUntil(this.destroy$),
    switchMap((data: HttpResponse<IGetCountries>) => {
      return data.body ? of(data.body?.result) : [];
    }),
  );

  ngOnInit(): void {
    this.items = this.generateMenuItems();
    const tokenData: IToken = this.authenticationService.extractToken();
    this.loggedUser = tokenData.fullname;
    this.selectedCountryId = +tokenData.countryId;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Generate menu items
   *
   * @private
   * @return {*}  {MenuItem[]}
   * @memberof HeaderComponent
   */
  private generateMenuItems(): MenuItem[] {
    return [
      {
        label: this.translocoService.translate('core.menu.items.vehicles'),
        id: MenuBarItemEnum.Vehicles,
        routerLink: [`/${MenuBarItemEnum.Vehicles}`],
      },
      {
        label: this.translocoService.translate('core.menu.items.offers'),
        id: MenuBarItemEnum.Offers,
        routerLink: [`/${MenuBarItemEnum.Offers}`],
      },
    ];
  }

  /**
   * Method navigates to homepage
   *
   * @memberof HeaderComponent
   */
  goHome(): void {
    this.router.navigateByUrl('/');
  }

  /**
   * The `logout` function logout the user from application by calling the `logout` method of the
   * `authenticationService`, and then redirects the user to the login page if the logout is successful,
   * or displays an error message if it fails.
   *
   * @memberof HeaderComponent
   */
  logout(): void {
    this.authenticationService.logout().then((valid: boolean) => {
      if (valid) {
        this.router.navigateByUrl('/login');
      } else {
        const title = this.translocoService.translate('errors.default.title');
        const message = this.translocoService.translate(
          'errors.default.message',
        );
        this.notificationService.showToast(title, message, SeverityEnum.Error);
      }
    });
  }

  editApplicationSettings(): void {
    this.applicationSettingsService
      .getApplicationSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: HttpResponse<IGetApplicationSettings>) => {
        if (data.status === HttpStatusCode.Ok) {
          this.applicationSettingsDialog(data.body);
        } else {
          this.errorService.responseErrorHandler(data.status);
        }
      });
  }

  checkUserRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.User) {
      return true;
    }
    return false;
  }

  checkSuperAdminRole(): boolean {
    const token: IToken = this.authenticationService.extractToken();
    const role = token.role;
    if (role === Roles.SuperAdmin) {
      return true;
    }
    return false;
  }

  applicationSettingsDialog(model?: IGetApplicationSettings | null): void {
    const ref: DynamicDialogRef = this.dialogService.open(
      ApplicationSettingsDialogComponent,
      {
        modal: true,
        data: {
          model: model,
        },
        closable: true,
        width: '50%',
        height: '100vh',
        contentStyle: { overflow: 'hidden' },
        baseZIndex: 10000,
        maximizable: false,
        styleClass: 'application-settings-dialog-top',
        header: this.translocoService.translate(
          'applicationSettings.labels.edit',
        ),
      },
    );

    ref.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IUpdateApplicationSettingsRequest | boolean) => {
        if (data) {
          if (typeof data !== 'boolean') {
            this.applicationSettingsService
              .updateApplicationSettings(data)
              .pipe(takeUntil(this.destroy$))
              .subscribe((data: HttpResponse<void>) => {
                if (data.status !== HttpStatusCode.Ok) {
                  this.errorService.responseErrorHandler(data.status);
                }
              });
          }
        }
      });
  }

  changeCountry(event: any): void {
    const refreshToken = this.authenticationService.getRefreshToken();
    const subscription: Subscription = this.countryService
      .changeCountry(event.value, refreshToken!)
      .subscribe((data: HttpResponse<any>) => {
        subscription.unsubscribe();
        if (data.status === HttpStatusCode.Ok) {
          sessionStorage.setItem('token', data.body?.result as string);
          window.location.reload();
        }
      });
  }

  isActive(item: any): boolean {
    return this.router.url === item.routerLink[0];
  }
}
